import ReactDOM from "react-dom";
import Screener from "./Screener";
import { DialogFooter } from "./Footer";
import { DialogHeader, AlertDialogHeader } from "./Header";
import Button from "./Button";
import useModal from "../hooks/useModal";
import useLang from "../hooks/useLang";
import styles from "./modal.module.css";

const IGNORE_OUTSIDE_CLICK = true;

const Modal = ({ children, isOpen, onClose }) => {
  const handleClick = (e) => {
    if (typeof onClose === "function") onClose();
  };
  if (!isOpen) return null;
  return ReactDOM.createPortal(
    <>
      <Screener onClick={IGNORE_OUTSIDE_CLICK ? undefined : handleClick} />
      <div className={styles.modal}>{children}</div>
    </>,
    document.getElementById("modal")
  );
};

const AlertModal = ({ children, isOpen, onClose, title }) => {
  const lang = useLang();
  const handleClick = (e) => {
    if (typeof onClose === "function") onClose();
  };
  if (!isOpen) return null;
  return ReactDOM.createPortal(
    <>
      <Screener onClick={IGNORE_OUTSIDE_CLICK ? undefined : handleClick} />
      <div className={styles.alertModal}>
        <AlertDialogHeader>
          <h2>{title}</h2>
        </AlertDialogHeader>
        {children}
        <DialogFooter>
          <Button onClick={onClose}>{lang("Close")}</Button>
        </DialogFooter>
      </div>
    </>,
    document.getElementById("alertModal")
  );
};

const AlertModalWithOverride = ({
  children,
  isOpen,
  onClose,
  onOverride,
  title,
}) => {
  const lang = useLang();
  const handleClick = (e) => {
    if (typeof onClose === "function") onClose();
  };
  if (!isOpen) return null;
  return ReactDOM.createPortal(
    <>
      <Screener onClick={IGNORE_OUTSIDE_CLICK ? undefined : handleClick} />
      <div className={styles.alertModal}>
        <AlertDialogHeader>
          <h2>{title}</h2>
        </AlertDialogHeader>
        {children}
        <DialogFooter>
          <Button onClick={onClose}>{lang("Close")}</Button>
          <Button onClick={onOverride} alert>
            {lang("OK")}
          </Button>
        </DialogFooter>
      </div>
    </>,
    document.getElementById("alertModal")
  );
};

const ModalHeader = ({ title }) => {
  if (title == null) return null;
  return (
    <DialogHeader>
      <h2>{title}</h2>
    </DialogHeader>
  );
};

const AlertModalHeader = ({ title }) => {
  if (title == null) return null;
  return (
    <AlertDialogHeader>
      <h2>{title}</h2>
    </AlertDialogHeader>
  );
};

const OkCancelFooter = ({ onSubmit, onCancel }) => {
  const lang = useLang();
  return (
    <DialogFooter>
      <Button onClick={onCancel}>{lang("Cancel")}</Button>
      <Button onClick={onSubmit} important>
        {lang("OK")}
      </Button>
    </DialogFooter>
  );
};

const SubmitCancelFooter = ({
  onSubmit,
  onCancel,
  submitLabel,
  cancelLabel,
  isProcessing,
  disabled,
  emphasizeCancel = false,
}) => {
  const lang = useLang();
  return (
    <DialogFooter>
      <Button onClick={onCancel} important={emphasizeCancel}>
        {cancelLabel ? cancelLabel : lang("Cancel")}
      </Button>
      <Button
        onClick={onSubmit}
        important={!emphasizeCancel}
        disabled={isProcessing || disabled}
      >
        {submitLabel ? submitLabel : lang("Submit")}
      </Button>
    </DialogFooter>
  );
};

const SubmitFooter = ({ onSubmit, submitLabel, isProcessing }) => {
  const lang = useLang();
  return (
    <DialogFooter>
      <Button onClick={onSubmit} important disabled={isProcessing}>
        {submitLabel ? submitLabel : lang("Submit")}
      </Button>
    </DialogFooter>
  );
};

const AlertOkCancelFooter = ({ onSubmit, onCancel, isLoading }) => {
  const lang = useLang();
  return (
    <DialogFooter>
      <Button onClick={onCancel} disabled={isLoading}>
        {lang("Cancel")}
      </Button>
      <Button onClick={onSubmit} disabled={isLoading} alert>
        {lang("OK")}
      </Button>
    </DialogFooter>
  );
};

const ModalOpener = ({
  children,
  renderOpen,
  renderFooter,
  onCancel,
  ...rest
}) => {
  const { isOpen, onOpen, onClose } = useModal({ onCancel });
  const props = { isOpen, onClose, ...rest };
  return (
    <>
      {typeof renderOpen === "function"
        ? renderOpen((event) => {
            if (typeof event?.preventDefault === "function")
              event?.preventDefault();
            onOpen();
          })
        : null}
      <Modal {...props}>
        {children}
        {typeof renderFooter === "function" ? renderFooter(onClose) : null}
      </Modal>
    </>
  );
};

const FormModal = ({
  children,
  title,
  submitLabel,
  cancelLabel,
  onSubmit,
  onCancel,
  renderOpen,
  isProcessing,
  disabled,
  ...rest
}) => {
  return (
    <>
      <ModalOpener
        renderOpen={renderOpen}
        onCancel={onCancel}
        {...rest}
        renderFooter={(onClose) => (
          <SubmitCancelFooter
            onSubmit={() => {
              if (typeof onSubmit === "function") onSubmit();
              if (typeof onClose === "function") onClose();
            }}
            onCancel={onClose}
            submitLabel={submitLabel}
            cancelLabel={cancelLabel}
            isProcessing={isProcessing}
            disabled={disabled}
          />
        )}
      >
        <ModalHeader title={title} />
        {children}
      </ModalOpener>
    </>
  );
};

const FormModalControlled = ({
  children,
  title,
  onSubmit,
  onCancel,
  onClose,
  submitLabel,
  cancelLabel,
  disabled,
  ...rest
}) => {
  return (
    <>
      <Modal {...rest}>
        <ModalHeader title={title} />
        {children}
        <SubmitCancelFooter
          onSubmit={() => {
            onSubmit();
            if (typeof onClose === "function") onClose();
          }}
          onCancel={onCancel}
          submitLabel={submitLabel}
          cancelLabel={cancelLabel}
          disabled={disabled}
        />
      </Modal>
    </>
  );
};

const ConfirmationModal = ({
  children,
  title,
  onSubmit,
  renderOpen,
  ...rest
}) => {
  return (
    <>
      <ModalOpener
        renderOpen={renderOpen}
        {...rest}
        renderFooter={(onCancel) => (
          <OkCancelFooter
            onSubmit={() => {
              onSubmit();
              onCancel();
            }}
            onCancel={onCancel}
          />
        )}
      >
        <ModalHeader title={title} />
        {children}
      </ModalOpener>
    </>
  );
};

const ConfirmationModalControlled = ({
  children,
  title,
  onSubmit,
  onCancel,
  submitLabel,
  cancelLabel,
  onClose,
  disabled,
  ...rest
}) => {
  return (
    <>
      <Modal {...rest}>
        <ModalHeader title={title} />
        {children}
        {typeof onCancel === "function" ? (
          <SubmitCancelFooter
            onSubmit={() => {
              onSubmit();
              if (typeof onClose === "function") onClose();
            }}
            emphasizeCancel={true}
            onCancel={onCancel}
            submitLabel={submitLabel}
            cancelLabel={cancelLabel}
            disabled={disabled}
          />
        ) : (
          <SubmitFooter onSubmit={onSubmit} submitLabel={submitLabel} />
        )}
      </Modal>
    </>
  );
};

const AlertConfirmationModal = ({
  children,
  title,
  onSubmit,
  renderOpen,
  isLoading,
  ...rest
}) => {
  const lang = useLang();
  return (
    <>
      <ModalOpener
        renderOpen={renderOpen}
        {...rest}
        renderFooter={(onCancel) => (
          <AlertOkCancelFooter
            onSubmit={onSubmit}
            onCancel={onCancel}
            isLoading={isLoading}
          />
        )}
      >
        <AlertModalHeader title={title} />
        {isLoading ? <p>{lang("loading")}</p> : <> {children}</>}
      </ModalOpener>
    </>
  );
};

export {
  AlertModal,
  AlertModalWithOverride,
  ConfirmationModal,
  ConfirmationModalControlled,
  AlertConfirmationModal,
  FormModal,
  FormModalControlled,
};
export default Modal;
